"use client";

import { useEffect, useMemo, useState } from "react";
import { addDays, format, parse, startOfDay } from "date-fns";
import { cn } from "@/lib/utils";
import type { MarketplaceDateFilterValue } from "@/types/api/marketplace";
import {
  filterChipInactiveClass,
  filterMobileDateFieldActiveClass,
  filterMobileDateFieldCardActive,
  filterMobileDateFieldCardFilled,
  filterMobileDateFieldCardInactive,
  filterMobileDateFieldInactiveClass,
  filterPrimaryButtonClass,
} from "./filterStyles";
import { MarketplaceSingleMonthCalendar } from "./MarketplaceSingleMonthCalendar";

type DateStep = "start" | "end";

interface MarketplaceMobileDatePickerPanelProps {
  label: string;
  value?: MarketplaceDateFilterValue;
  onChange: (value: MarketplaceDateFilterValue | undefined) => void;
  onApply?: () => void;
  isOpen?: boolean;
  className?: string;
}

function parseDate(iso?: string): Date | undefined {
  if (!iso) return undefined;
  const d = parse(iso, "yyyy-MM-dd", new Date());
  return Number.isNaN(d.getTime()) ? undefined : d;
}

function toIso(date?: Date): string | undefined {
  if (!date) return undefined;
  return format(date, "yyyy-MM-dd");
}

function formatPillDate(date?: Date) {
  if (!date) return null;
  return format(date, "EEE, MMM d");
}

export function MarketplaceMobileDatePickerPanel({
  value,
  onChange,
  onApply,
  isOpen = true,
  className,
}: MarketplaceMobileDatePickerPanelProps) {
  const [step, setStep] = useState<DateStep>("start");
  const [startDate, setStartDate] = useState<Date | undefined>();
  const [endDate, setEndDate] = useState<Date | undefined>();

  const today = useMemo(() => startOfDay(new Date()), []);
  const tomorrow = useMemo(() => addDays(today, 1), [today]);

  useEffect(() => {
    if (!isOpen) return;
    const parsedStart = parseDate(value?.start);
    const parsedEnd = parseDate(value?.end);
    setStartDate(parsedStart);
    setEndDate(parsedEnd);
    if (parsedStart && !parsedEnd) {
      setStep("end");
    } else {
      setStep("start");
    }
  }, [isOpen, value?.start, value?.end]);

  const handleStartSelect = (date: Date | undefined) => {
    if (!date) return;
    setStartDate(date);
    if (endDate && endDate < date) {
      setEndDate(undefined);
    }
    setStep("end");
  };

  const handleEndSelect = (date: Date | undefined) => {
    if (!date || !startDate) return;
    const resolvedEnd = date < startDate ? startDate : date;
    setEndDate(resolvedEnd);
  };

  const handleClear = () => {
    setStartDate(undefined);
    setEndDate(undefined);
    setStep("start");
    onChange(undefined);
    onApply?.();
  };

  const handleApply = () => {
    if (!startDate || !endDate) return;
    onChange({
      start: toIso(startDate),
      end: toIso(endDate),
    });
    onApply?.();
  };

  const canApply = Boolean(startDate && endDate);

  const applyQuickDate = (date: Date) => {
    if (step === "start") {
      handleStartSelect(date);
      return;
    }
    if (!startDate) return;
    handleEndSelect(date);
  };

  const startPillLabel = formatPillDate(startDate) ?? "Add date";
  const endPillLabel = formatPillDate(endDate) ?? "Add date";

  const calendarMonth =
    step === "start" ? (startDate ?? today) : (endDate ?? startDate ?? today);

  const calendarDisabled = useMemo(() => {
    if (step === "start") {
      return { before: today };
    }
    const minEnd = startDate && startDate > today ? startDate : today;
    return { before: minEnd };
  }, [step, startDate, today]);

  return (
    <div
      className={cn(
        "flex min-h-0 flex-1 flex-col gap-3 overflow-hidden",
        className
      )}>
      <div className="grid shrink-0 grid-cols-2 gap-2">
        <button
          type="button"
          onClick={() => setStep("start")}
          className={cn(
            "text-left transition-colors",
            step === "start"
              ? filterMobileDateFieldCardActive
              : startDate
                ? filterMobileDateFieldCardFilled
                : filterMobileDateFieldCardInactive
          )}>
          <span
            className={cn(
              "block text-xs leading-4 font-medium",
              step === "start"
                ? cn(filterMobileDateFieldActiveClass, "font-semibold")
                : filterMobileDateFieldInactiveClass
            )}>
            From
          </span>
          <span
            className={cn(
              "mt-1 block truncate text-sm leading-5",
              startDate
                ? "font-medium text-gray-900"
                : "font-normal text-gray-500"
            )}>
            {startPillLabel}
          </span>
        </button>

        <button
          type="button"
          onClick={() => startDate && setStep("end")}
          disabled={!startDate}
          className={cn(
            "text-left transition-colors",
            step === "end"
              ? filterMobileDateFieldCardActive
              : endDate
                ? filterMobileDateFieldCardFilled
                : filterMobileDateFieldCardInactive,
            !startDate && "cursor-not-allowed opacity-60"
          )}>
          <span
            className={cn(
              "block text-xs leading-4 font-medium",
              step === "end"
                ? cn(filterMobileDateFieldActiveClass, "font-semibold")
                : filterMobileDateFieldInactiveClass,
              !startDate && "text-gray-400"
            )}>
            To
          </span>
          <span
            className={cn(
              "mt-1 block truncate text-sm leading-5",
              endDate
                ? "font-medium text-gray-900"
                : "font-normal text-gray-500",
              !startDate && "text-gray-400"
            )}>
            {endPillLabel}
          </span>
        </button>
      </div>

      <div className="flex shrink-0 gap-2">
        <button
          type="button"
          onClick={() => applyQuickDate(today)}
          className={cn(filterChipInactiveClass, "px-3 py-1.5 text-sm")}>
          Today
        </button>
        <button
          type="button"
          onClick={() => applyQuickDate(tomorrow)}
          className={cn(filterChipInactiveClass, "px-3 py-1.5 text-sm")}>
          Tomorrow
        </button>
      </div>

      <div className="min-h-[16rem] shrink-0">
        <MarketplaceSingleMonthCalendar
          key={`${step}-${isOpen ? "open" : "closed"}-${calendarMonth.toISOString()}`}
          selected={step === "start" ? startDate : endDate}
          onSelect={step === "start" ? handleStartSelect : handleEndSelect}
          defaultMonth={calendarMonth}
          disabled={calendarDisabled}
        />
      </div>

      <div className="mt-auto flex shrink-0 items-center justify-between gap-3 border-t border-gray-100 pt-3">
        <button
          type="button"
          onClick={handleClear}
          className="cursor-pointer text-sm font-medium text-gray-600 underline hover:text-gray-900">
          Clear
        </button>
        <button
          type="button"
          onClick={handleApply}
          disabled={!canApply}
          className={cn(
            filterPrimaryButtonClass,
            "shrink-0 px-6 py-2.5 disabled:cursor-not-allowed disabled:opacity-50"
          )}>
          Done
        </button>
      </div>
    </div>
  );
}
